
import React from 'react';
import { AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface DnsInstructionsProps {
  domain: string;
  status: string;
  domainId: string;
  isVerifying: string | null;
  verificationError: string | null;
  dnsCheckResult: any;
  manualVerification: boolean;
  verifyDomainManually: (domainId: string) => Promise<void>;
}

export const DnsInstructions: React.FC<DnsInstructionsProps> = ({
  domain,
  status,
  domainId,
  isVerifying,
  verificationError,
  dnsCheckResult,
  manualVerification,
  verifyDomainManually,
}) => {
  if (status === 'active') {
    return (
      <div className="flex items-start gap-2 bg-green-50 p-3 rounded-md">
        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
        <div>
          <p className="font-medium text-green-700">Domaine actif</p>
          <p>Votre boutique est accessible à l'adresse <a href={`https://${domain}`} target="_blank" className="underline">https://{domain}</a></p>
        </div>
      </div>
    );
  }

  if (status === 'verified') {
    return (
      <div className="flex items-start gap-2 bg-blue-50 p-3 rounded-md">
        <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
        <div>
          <p className="font-medium text-blue-700">DNS vérifié</p>
          <p>Votre domaine est correctement configuré et sera activé sous peu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 bg-yellow-50 p-4 rounded-md">
      <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 shrink-0" />
      <div>
        <p className="font-medium text-yellow-700 mb-2">
          {verificationError && isVerifying === domainId 
            ? "Configuration DNS incorrecte" 
            : "Configuration DNS requise"}
        </p>
        
        <div className="space-y-4">
          {verificationError && isVerifying === domainId && (
            <div className="p-3 bg-red-50 rounded-md border border-red-200 mb-4">
              <p className="font-medium text-red-700">Erreur détectée</p>
              <p className="text-red-600">{verificationError}</p>
              {dnsCheckResult && (
                <div className="mt-2">
                  <p className="font-medium text-red-700 mt-2">Détails techniques</p>
                  <p className="text-red-600 text-xs font-mono break-all">
                    {dnsCheckResult.cname?.Answer ? (
                      `CNAME trouvé: ${JSON.stringify(dnsCheckResult.cname.Answer[0]?.data || "Non trouvé")}`
                    ) : "Aucun enregistrement CNAME trouvé"}
                  </p>
                </div>
              )}
            </div>
          )}

          <ol className="list-decimal pl-5 space-y-3">
            <li>
              <p className="font-medium">Connectez-vous à votre hébergeur de domaine</p>
              <p className="text-sm text-gray-600">Accédez au panneau de gestion de votre fournisseur de domaine (GoDaddy, OVH, Namecheap, etc.)</p>
            </li>
            
            <li>
              <p className="font-medium">Accédez à la section gestion DNS</p>
              <p className="text-sm text-gray-600">Recherchez une section appelée "Gestion DNS", "Zone DNS", "Enregistrements DNS" ou similaire</p>
            </li>
            
            <li>
              <p className="font-medium">Configurez vos enregistrements DNS</p>
              <p className="text-sm text-gray-600">Ajoutez les enregistrements suivants:</p>
              
              <div className="mt-2 overflow-x-auto">
                <table className="w-full border-collapse mt-2 text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left">Type</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Nom/Hôte</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">Valeur/Cible</th>
                      <th className="border border-gray-300 px-3 py-2 text-left">TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="border border-gray-300 px-3 py-2 font-semibold">CNAME</td>
                      <td className="border border-gray-300 px-3 py-2">www</td>
                      <td className="border border-gray-300 px-3 py-2">scaloo.vercel.app</td>
                      <td className="border border-gray-300 px-3 py-2">14400</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-3 py-2 font-semibold">A</td>
                      <td className="border border-gray-300 px-3 py-2">@</td>
                      <td className="border border-gray-300 px-3 py-2">76.76.21.21</td>
                      <td className="border border-gray-300 px-3 py-2">60</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <p className="font-medium text-amber-700">Important:</p>
                <ul className="list-disc pl-5 text-sm text-amber-600">
                  <li className="mt-1">L'enregistrement CNAME pour "www" pointe vers scaloo.vercel.app</li>
                  <li className="mt-1">L'enregistrement A pour "@" (apex domain) pointe vers l'adresse IP de Vercel: 76.76.21.21</li>
                </ul>
              </div>
            </li>
            
            <li>
              <p className="font-medium">Patientez et vérifiez</p>
              <p className="text-sm text-gray-600">La propagation DNS peut prendre de quelques minutes à 24h selon votre hébergeur. Une fois terminée, cliquez sur "Vérifier".</p>
            </li>
          </ol>
          
          <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
            <p className="text-sm text-blue-700 font-medium mb-1">Besoin d'aide?</p>
            <p className="text-xs text-blue-600">Chaque hébergeur a une interface différente. Si vous ne trouvez pas où configurer votre DNS:</p>
            <ul className="list-disc pl-5 text-xs text-blue-600 mt-1">
              <li>Recherchez "Configurer DNS [nom de votre hébergeur]" sur Google</li>
              <li>Consultez la documentation de votre hébergeur</li>
              <li>Contactez le support de votre hébergeur</li>
            </ul>
          </div>
          
          {manualVerification && (
            <div className="mt-4">
              <button
                onClick={() => verifyDomainManually(domainId)}
                disabled={isVerifying === domainId}
                className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium py-2 px-3 rounded-md transition-colors"
              >
                Vérifier manuellement
              </button>
            </div>
          )}
          
          <div className="mt-2">
            <a 
              href={`https://dnschecker.org/cname-lookup.php?query=${domain}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              <span>Vérifier la propagation DNS</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DnsInstructions;
